import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { initPixel, trackPageView } from "@/lib/metaPixel";

import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PendingVerification from "@/pages/PendingVerification";
import Dashboard from "@/pages/Dashboard";
import OrderFlow from "@/pages/OrderFlow";
import AdminPanel from "@/pages/AdminPanel";

const Loading = () => (
  <div className="min-h-screen bg-cream flex items-center justify-center">
    <span className="font-serif text-2xl text-gold">Canto Dei</span>
  </div>
);

// Initialisiert den Meta Pixel einmalig und sendet bei jedem Seitenwechsel ein PageView.
function PixelTracker() {
  const location = useLocation();
  useEffect(() => {
    initPixel();
  }, []);
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  return null;
}

function RequireCustomer({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.status === "verified") return children;
  return <Navigate to="/konto-pruefung" replace />;
}

function RequirePending({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.status === "verified") return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PixelTracker />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/registrieren" element={<Register />} />
            <Route path="/passwort-vergessen" element={<ForgotPassword />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
            <Route
              path="/konto-pruefung"
              element={
                <RequirePending>
                  <PendingVerification />
                </RequirePending>
              }
            />
            <Route
              path="/dashboard"
              element={
                <RequireCustomer>
                  <Dashboard />
                </RequireCustomer>
              }
            />
            <Route
              path="/trauersong-erstellen"
              element={
                <RequireCustomer>
                  <OrderFlow />
                </RequireCustomer>
              }
            />
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminPanel />
                </RequireAdmin>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </div>
  );
}

export default App;
